import { Popover } from "@base-ui/react/popover";
import { Checkbox } from "@base-ui/react/checkbox";
import { CheckboxGroup } from "@base-ui/react/checkbox-group";
import { useMemo, useState } from "react";
import { Menu } from "lucide-react";
import styles from "./FilterPopover.module.css";
import {
  DEGREE_OPTIONS,
  SUBFIELD_OPTIONS,
} from "../../stores/recommendations/mentorRecommendationsSlice";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import cx from "classnames";

interface FilterPopoverProps {
  showDegree?: boolean;
  showSubfield?: boolean;
  selectedSubfields?: string[];
  selectedDegrees?: string[];
  onSubfieldsChange?: (subfields: string[]) => void;
  onDegreesChange?: (degrees: string[]) => void;
  onClear?: () => void;
  degreeOptions?: string[];
  subfieldOptions?: string[];
}

export function ResourceFilterPopover({
  showDegree = false,
  showSubfield = false,
  selectedSubfields = [],
  selectedDegrees = [],
  onSubfieldsChange,
  onDegreesChange,
  onClear,
  degreeOptions = DEGREE_OPTIONS,
  subfieldOptions = SUBFIELD_OPTIONS,
}: FilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const [subfieldSearch, setSubfieldSearch] = useState("");

  const activeFilterCount =
    (showDegree ? selectedDegrees.length : 0) +
    (showSubfield ? selectedSubfields.length : 0);

  const hasActiveFilters = activeFilterCount > 0;

  const filteredSubfields = useMemo(() => {
    const search = subfieldSearch.trim().toLowerCase();

    if (!search) {
      return subfieldOptions;
    }

    return subfieldOptions.filter((subfield) =>
      subfield.toLowerCase().includes(search),
    );
  }, [subfieldOptions, subfieldSearch]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
  };

  const handleClearAll = () => {
    setSubfieldSearch("");
    onClear?.();
  };

  if (!showDegree && !showSubfield) {
    return null;
  }

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger className={styles.trigger} data-cy="filter-popover">
        <Menu className={styles.triggerIcon} strokeWidth={1.5} />

        <span>Filters</span>

        {hasActiveFilters && (
          <span className={styles.count}>{activeFilterCount}</span>
        )}

        <span
          className={cx(styles.chevron, open ? styles.chevronOpen : "")}
          aria-hidden="true"
        >
          <ArrowDropDownIcon />
        </span>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Positioner
          sideOffset={8}
          align="end"
          className={styles.positioner}
        >
          <Popover.Popup className={styles.popup}>
            <div className={styles.header}>
              <Popover.Title className={styles.title}>
                Filter resources
              </Popover.Title>

              <div className={styles.headerActions}>
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={handleClearAll}
                  disabled={!hasActiveFilters}
                  data-cy="clear-filters-button"
                >
                  Clear all
                </button>
              </div>
            </div>

            {showDegree && (
              <section className={styles.section}>
                <div className={styles.sectionLabel}>Degree level</div>

                <CheckboxGroup
                  value={selectedDegrees}
                  onValueChange={onDegreesChange}
                  className={styles.degreeGroup}
                  data-cy="degree-filter"
                >
                  {degreeOptions.map((degree) => (
                    <label
                      key={degree}
                      className={styles.checkboxRowInline}
                      data-cy="degree-filter-option"
                      data-value={degree}
                    >
                      <Checkbox.Root
                        name="degree"
                        value={degree}
                        className={styles.checkbox}
                      >
                        <Checkbox.Indicator className={styles.indicator}>
                          ✓
                        </Checkbox.Indicator>
                      </Checkbox.Root>

                      <span>{degree}</span>
                    </label>
                  ))}
                </CheckboxGroup>
              </section>
            )}

            {showSubfield && (
              <section className={styles.section}>
                <div className={styles.sectionLabel}>Subfield</div>

                <div className={styles.searchWrapper}>
                  <span className={styles.searchIcon}>⌕</span>

                  <input
                    className={styles.searchInput}
                    value={subfieldSearch}
                    onChange={(event) => {
                      setSubfieldSearch(event.target.value);
                    }}
                    placeholder="Search subfields..."
                    data-cy="subfield-search"
                  />
                </div>

                <CheckboxGroup
                  value={selectedSubfields}
                  onValueChange={onSubfieldsChange}
                  className={styles.subfieldList}
                  data-cy="subfield-filter"
                >
                  {filteredSubfields.map((subfield) => (
                    <label
                      key={subfield}
                      className={styles.checkboxRow}
                      data-cy="subfield-filter-option"
                      data-value={subfield}
                    >
                      <Checkbox.Root
                        name="subfield"
                        value={subfield}
                        className={styles.checkbox}
                      >
                        <Checkbox.Indicator className={styles.indicator}>
                          ✓
                        </Checkbox.Indicator>
                      </Checkbox.Root>

                      <span>{subfield}</span>
                    </label>
                  ))}

                  {filteredSubfields.length === 0 && (
                    <div className={styles.emptyState}>
                      No matching subfields
                    </div>
                  )}
                </CheckboxGroup>
              </section>
            )}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default ResourceFilterPopover;
